import React, { useRef, useEffect, useCallback } from 'react';
import { ApplicationView } from '@aha-app/mvc';
import styles from './ArtifactPanel.module.css';
import { loadMonacoEditor } from 'components/loadMonacoEditor';

const contentTypeToLanguage = contentType => {
  switch (contentType) {
    case 'text/javascript':
    case 'text/typescript':
    case 'application/javascript':
    case 'application/typescript':
    case 'application/vnd.aha.react':
      return 'typescript';

    case 'text/markdown':
      return 'markdown';

    case 'text/html':
      return 'html';

    default:
      return 'plaintext';
  }
};

const SourceView = ({ toolCall }) => {
  const editorContainerRef = useRef<HTMLDivElement>(null);
  const editor = useRef<any>(); // We have to use 'any' here because Monaco is not imported yet.
  const lastContentRef = useRef<string>('');
  const isStreamingRef = useRef<boolean>(false);

  const updateEditorContent = useCallback((content: string) => {
    if (!editor.current || lastContentRef.current === content) return;

    const model = editor.current.getModel();
    if (!model) return;

    const oldContent = lastContentRef.current;

    // Check if this is an append operation (common during streaming)
    if (content.startsWith(oldContent)) {
      // Only append the new content
      const newContent = content.substring(oldContent.length);
      if (newContent) {
        const lastLine = model.getLineCount();
        const lastColumn = model.getLineMaxColumn(lastLine);

        model.pushEditOperations(
          [],
          [
            {
              range: {
                startLineNumber: lastLine,
                startColumn: lastColumn,
                endLineNumber: lastLine,
                endColumn: lastColumn,
              },
              text: newContent,
            },
          ],
          () => null
        );
      }
    } else {
      // Full content replacement when content doesn't just append
      const fullRange = model.getFullModelRange();
      model.pushEditOperations(
        [],
        [
          {
            range: fullRange,
            text: content,
          },
        ],
        () => null
      );
    }

    // Auto-scroll to bottom during streaming
    if (isStreamingRef.current) {
      const lineCount = model.getLineCount();
      editor.current.revealLine(lineCount, 1); // 1 = ScrollType.Smooth
    }

    lastContentRef.current = content;
  }, []);

  useEffect(() => {
    if (editorContainerRef.current) {
      const importEditor = async () => {
        // Dyanamically import the editor.
        const monaco = await loadMonacoEditor();

        // We got unmounted during the import.
        if (!editorContainerRef.current) return;

        // Mount the editor.
        const newEditor = monaco.editor.create(editorContainerRef.current, {
          fixedOverflowWidgets: true,
          automaticLayout: true,
          lineNumbers: 'on',
          folding: false,
          wordWrap: 'on',
          wrappingIndent: 'deepIndent',
          tabSize: 2,
          insertSpaces: true,
          minimap: { enabled: false },
          value: toolCall.content.panel.body,
          language: contentTypeToLanguage(toolCall.content.panel.contentType),
          theme: 'vs',
          readOnly: toolCall.status === 'preparing',
          scrollBeyondLastLine: false,
          smoothScrolling: true,
          padding: { bottom: 50 },
        });

        lastContentRef.current = toolCall.content.panel.body;

        newEditor.onDidChangeModelContent(() => {
          if (!isStreamingRef.current) {
            toolCall.content.panel.body = newEditor.getValue();
            // TODO: Send changes to the server too. The simplest approach would
            // be to update this specific version of the artifact.
          }
        });
        newEditor.focus();
        editor.current = newEditor;
      };

      void importEditor();

      return () => {
        if (editor.current) {
          editor.current.dispose();
          editor.current = undefined;
        }
      };
    }
    // We don't need all the dependencies here since the editor is updated by the useEffect below when necessary.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editorContainerRef, updateEditorContent]);

  // Handle streaming updates
  useEffect(() => {
    isStreamingRef.current = toolCall.status === 'preparing';

    if (editor.current) {
      if (toolCall.status === 'preparing') {
        // Direct updates during streaming
        updateEditorContent(toolCall.content.panel.body);
      } else if (toolCall.status === 'complete') {
        // Final update when streaming completes
        updateEditorContent(toolCall.content.panel.body);

        if (editor.current.getRawOptions().readOnly) {
          editor.current.updateOptions({
            readOnly: false,
          });
        }
      }
    }
  }, [toolCall.content.panel.body, toolCall.status, updateEditorContent]);

  if (!toolCall || !toolCall.content || !toolCall.content.panel) return null;

  return <div ref={editorContainerRef} className={styles.sourceView} />;
};

export default ApplicationView(SourceView);
